import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchEmployeeCases,
  fetchEmployeeList,
} from "../../store/allocation/employeeDashboardSlice";
import { useEmployeeDashboardAccess } from "../../hooks/useEmployeeDashboardAccess";
import { DASHBOARD } from "../../components/employee-dashboard/dashboardTheme";
import { caseHrefFromAllocationId } from "../../utils/caseIds";
import {
  CaseTypeSelect,
  CustomEmployeeSelect,
  DashboardPagination,
  DashboardSearchInput,
  DashboardTableCard,
  portalTableCell,
  portalTableHead,
} from "../../components/employee-dashboard/DashboardFilterControls";

const statusColor = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s.includes("completed")) return "bg-blue-500";
  if (s.includes("hold")) return "bg-red-500";
  if (s.includes("ready")) return "bg-green-500";
  if (s.includes("allocated")) return "bg-orange-400";
  return "bg-gray-500";
};

const EmployeeCases = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { access, canViewAll, employee } = useEmployeeDashboardAccess();
  const { cases, page, totalPages, casesLoading, error, employees } =
    useAppSelector((state) => state.employeeDashboard);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [localPage, setLocalPage] = useState(1);
  const [pickerReady, setPickerReady] = useState(!canViewAll);

  const empIdFromUrl = searchParams.get("empId")
    ? Number(searchParams.get("empId"))
    : null;
  const caseTypeFromUrl = searchParams.get("caseType") || "";

  const filterEmpId = useMemo(() => {
    if (canViewAll) return empIdFromUrl;
    return employee?.emp_id ?? null;
  }, [canViewAll, empIdFromUrl, employee?.emp_id]);

  const caseType = caseTypeFromUrl;

  const setFilters = useCallback(
    (next: { empId?: number | ""; caseType?: string }) => {
      const params = new URLSearchParams(searchParams);
      if (next.empId !== undefined) {
        if (next.empId) params.set("empId", String(next.empId));
        else params.delete("empId");
      }
      if (next.caseType !== undefined) {
        if (next.caseType) params.set("caseType", next.caseType);
        else params.delete("caseType");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.emp_id === filterEmpId),
    [employees, filterEmpId],
  );

  const displayName = canViewAll
    ? selectedEmployee?.emp_name || "All employees"
    : employee?.emp_name || "My cases";

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!access || !canViewAll) return;
    dispatch(fetchEmployeeList({ ...access })).finally(() =>
      setPickerReady(true),
    );
  }, [access, canViewAll, dispatch]);

  useEffect(() => {
    if (!access) return;
    if (!canViewAll && !filterEmpId) return;
    dispatch(
      fetchEmployeeCases({
        ...access,
        empId: filterEmpId,
        page: localPage,
        search: debouncedSearch,
        caseType: caseType || undefined,
      }),
    );
  }, [
    access,
    dispatch,
    canViewAll,
    filterEmpId,
    localPage,
    debouncedSearch,
    caseType,
  ]);

  useEffect(() => {
    setLocalPage(1);
  }, [debouncedSearch, filterEmpId, caseType]);

  const showEmployeeCol = canViewAll && !filterEmpId;
  const colSpan = showEmployeeCol ? 12 : 11;
  const canLoad = canViewAll || !!filterEmpId;

  return (
    <>
      <Helmet>
        <title>{displayName} · Cases</title>
      </Helmet>

      <div className="min-h-full w-full min-w-0 overflow-x-hidden">
        <div className="flex flex-col gap-4 w-full min-w-0">
          {error ? (
            <div className="text-red-500 bg-white border border-red-200 rounded-2xl p-3">
              {error}
            </div>
          ) : null}

          <div
            className="rounded-2xl px-4 py-4 md:px-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 min-w-0"
            style={{ backgroundColor: DASHBOARD.header }}
          >
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-white truncate">
                {displayName}
              </h1>
              <p className="text-white/80 text-xs sm:text-sm mt-0.5">
                {canViewAll
                  ? "All allocated cases. Filled orders are one row per patient that employee did."
                  : "Your allocated cases. Filled orders show that patient's units."}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full lg:w-auto min-w-0">
              {canViewAll ? (
                <CustomEmployeeSelect
                  team={employees}
                  value={filterEmpId ?? ""}
                  onChange={(id) => setFilters({ empId: id })}
                  disabled={!pickerReady}
                  allowAll
                  variant="header"
                  className="w-full sm:w-[220px]"
                />
              ) : null}
              <CaseTypeSelect
                value={caseType}
                onChange={(value) => setFilters({ caseType: value })}
                variant="header"
                className="w-full sm:w-[180px]"
              />
              <DashboardSearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search Case ID / Patient / Owner…"
                className="w-full sm:w-[280px]"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-[#8A8A85]">
              {casesLoading
                ? "Loading cases…"
                : `${cases.length} case${cases.length === 1 ? "" : "s"} on this page`}
            </p>
            <DashboardPagination
              page={page}
              totalPages={totalPages}
              onPrev={() => setLocalPage((p) => Math.max(1, p - 1))}
              onNext={() => setLocalPage((p) => p + 1)}
            />
          </div>

          <DashboardTableCard>
            <table className="table-auto w-full text-sm">
              <thead>
                <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                  <th className={portalTableHead}>Case ID</th>
                  <th className={portalTableHead}>Patient</th>
                  {showEmployeeCol ? (
                    <th className={portalTableHead}>Employee</th>
                  ) : null}
                  <th className={portalTableHead}>Kind</th>
                  <th className={portalTableHead}>Work</th>
                  <th className={portalTableHead}>Case Owner</th>
                  <th className={portalTableHead}>Allocation</th>
                  <th className={portalTableHead}>Designer</th>
                  <th className={portalTableHead}>Captain</th>
                  <th className={portalTableHead}>Design Units</th>
                  <th className={portalTableHead}>QC Units</th>
                  <th className={portalTableHead}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {!canLoad ? (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className={`${portalTableCell} py-8 text-center text-[#8A8A85]`}
                    >
                      Employee session required.
                    </td>
                  </tr>
                ) : casesLoading ? (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className={`${portalTableCell} py-8 text-center text-[#8A8A85]`}
                    >
                      Loading cases…
                    </td>
                  </tr>
                ) : cases.length === 0 ? (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className={`${portalTableCell} py-8 text-center text-[#8A8A85]`}
                    >
                      No cases found.
                    </td>
                  </tr>
                ) : (
                  cases.map((item, index) => (
                    <tr
                      key={`${item.case_id}-${item.patient_name || ""}-${index}`}
                      className={`border-b border-[#F3F3F0] ${
                        index % 2 !== 0 ? "bg-[#FDFBF2]/60" : "bg-white"
                      } hover:bg-[#93B9AD]/10 transition-colors`}
                    >
                      <td className={portalTableCell}>
                        <a
                          href={caseHrefFromAllocationId(item.case_id)}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium underline"
                          style={{ color: DASHBOARD.header }}
                        >
                          {item.case_id}
                        </a>
                      </td>
                      <td
                        className={`${portalTableCell} break-words whitespace-normal max-w-[14rem]`}
                      >
                        {item.patient_name || "—"}
                      </td>
                      {showEmployeeCol ? (
                        <td className={portalTableCell}>
                          {item.employee_name || item.designer || "—"}
                        </td>
                      ) : null}
                      <td className={portalTableCell}>
                        {item.units_family_label || "Live"}
                      </td>
                      <td className={portalTableCell}>
                        {item.units_work || "—"}
                      </td>
                      <td
                        className={`${portalTableCell} break-words whitespace-normal max-w-[12rem]`}
                      >
                        {item.case_owner}
                      </td>
                      <td className={portalTableCell}>
                        <span
                          className={`inline-flex items-center justify-center text-xs text-white py-1 px-3 rounded-full ${statusColor(
                            item.allocation_status,
                          )}`}
                        >
                          {item.allocation_status}
                        </span>
                      </td>
                      <td className={portalTableCell}>
                        {item.designer || "—"}
                      </td>
                      <td className={portalTableCell}>
                        {item.captain || "—"}
                      </td>
                      <td className={portalTableCell}>{item.design_units}</td>
                      <td className={portalTableCell}>{item.qc_units}</td>
                      <td className={portalTableCell}>
                        {item.updated_at
                          ? new Date(item.updated_at).toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </DashboardTableCard>
        </div>
      </div>
    </>
  );
};

export default EmployeeCases;
